// [START OF FILE]
import ConnectCanvas from "./ConnectCanvas";
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AlertTriangle, Clock, CheckCircle, LogOut, Search, X } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Dashboard.css';
import { auth } from './firebase';
import Onboarding from './components/Onboarding';

import { PriorityTasks, ProgressRow, AssignmentFeed, AIHelper } from './components/DashboardSections';
// DashboardHeaderAndAlerts intentionally removed — header rebuilt inline below
const API_BASE = 'https://duequeue-backend.onrender.com'

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

// --- streak helpers ---
const STREAK_KEY = 'cs_streak';
function loadStreak() {
  try { const raw = localStorage.getItem(STREAK_KEY); return raw ? JSON.parse(raw) : { count: 0, lastDate: null }; }
  catch { return { count: 0, lastDate: null }; }
}
function saveStreak(s) { try { localStorage.setItem(STREAK_KEY, JSON.stringify(s)); } catch {} }
function isSameDay(a, b) { return a && b && new Date(a).toDateString() === new Date(b).toDateString(); }
function isYesterday(a, b) { const da = new Date(a), db = new Date(b); const y = new Date(db); y.setDate(db.getDate() - 1); return da.toDateString() === y.toDateString(); }

// --- time estimate heuristic ---
// Each incomplete assignment this week = ~2 hrs estimated, overdue = ~1 hr (already late, assume minimal)
function estimateWeeklyHours(incompleteThisWeek, overdueCount) {
  return (incompleteThisWeek * 2) + (overdueCount * 1);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function getUrgencyLabel(hoursLeft) {
  if (hoursLeft < 0) return { label: 'Overdue', color: '#ef4444' };
  if (hoursLeft < 24) return { label: 'Due today', color: '#f97316' };
  if (hoursLeft < 72) return { label: 'Due soon', color: '#eab308' };
  return { label: 'Upcoming', color: '#22c55e' };
}

// Inline styles — design system tokens
const tokens = {
  bg: '#0f1117',
  surface: '#161b27',
  surfaceHover: '#1c2333',
  border: 'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.13)',
  text: '#e8eaf0',
  textMuted: '#6b7280',
  textDim: '#9ca3af',
  accent: '#6366f1',
  accentLight: 'rgba(99,102,241,0.15)',
  accentGlow: 'rgba(99,102,241,0.3)',
  success: '#10b981',
  successLight: 'rgba(16,185,129,0.12)',
  warning: '#f59e0b',
  warningLight: 'rgba(245,158,11,0.12)',
  danger: '#ef4444',
  dangerLight: 'rgba(239,68,68,0.12)',
  orange: '#f97316',
  orangeLight: 'rgba(249,115,22,0.12)',
  radius: '14px',
  radiusSm: '8px',
  shadow: '0 1px 3px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.3)',
  shadowLg: '0 8px 32px rgba(0,0,0,0.5)',
};

const styles = {
  page: {
    minHeight: '100vh',
    background: tokens.bg,
    color: tokens.text,
    fontFamily: '"DM Sans", "Geist", ui-sans-serif, system-ui, sans-serif',
    padding: '0 0 80px 0',
  },

  // ── Top bar ──────────────────────────────────────────────────────
  topbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    padding: '18px 32px',
    borderBottom: `1px solid ${tokens.border}`,
    background: 'rgba(15,17,23,0.9)',
    backdropFilter: 'blur(12px)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: 700,
    fontSize: 18,
    letterSpacing: '-0.3px',
    color: tokens.text,
  },
  logoIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    background: `linear-gradient(135deg, ${tokens.accent}, #818cf8)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },
  topbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  darkToggleBtn: {
    background: 'transparent',
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    color: tokens.textDim,
    fontSize: 12,
    padding: '6px 11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },

  // ── User identity pill ───────────────────────────────────────────
  userPill: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 10px 5px 5px',
    background: 'rgba(255,255,255,0.04)',
    border: `1px solid ${tokens.border}`,
    borderRadius: 9,
  },
  userAvatar: {
    width: 26,
    height: 26,
    borderRadius: 7,
    background: `linear-gradient(135deg, ${tokens.accent}, #818cf8)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  userEmail: {
    fontSize: 12,
    fontWeight: 500,
    color: tokens.textDim,
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  // ── Bell button ──────────────────────────────────────────────────
  bellBtn: (hasAlerts) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    background: hasAlerts ? 'rgba(239,68,68,0.07)' : 'rgba(255,255,255,0.04)',
    border: `1px solid ${hasAlerts ? 'rgba(239,68,68,0.25)' : tokens.border}`,
    borderRadius: 9,
    color: hasAlerts ? '#f87171' : tokens.textDim,
    cursor: 'pointer',
    transition: 'all 0.15s',
    flexShrink: 0,
  }),
  bellDot: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: tokens.danger,
    border: '1.5px solid rgba(15,17,23,0.9)',
  },

  // ── Logout button ────────────────────────────────────────────────
  logoutBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '7px 11px',
    background: 'transparent',
    border: `1px solid ${tokens.border}`,
    borderRadius: 8,
    color: tokens.textMuted,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },

  // ── Alerts dropdown panel ────────────────────────────────────────
  alertsPanel: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    width: 'min(340px, calc(100vw - 32px))',
    background: '#1a2035',
    border: `1px solid ${tokens.borderStrong}`,
    borderRadius: tokens.radius,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    zIndex: 200,
    overflow: 'hidden',
  },
  alertsPanelHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  alertsPanelTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: tokens.textDim,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  alertsCloseBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    background: 'none',
    border: 'none',
    color: tokens.textMuted,
    cursor: 'pointer',
    padding: 3,
    borderRadius: 4,
  },
  alertChip: (variant) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '11px 16px',
    borderBottom: `1px solid ${tokens.border}`,
    background: variant === 'danger'
      ? 'rgba(239,68,68,0.05)'
      : variant === 'warning'
      ? 'rgba(249,115,22,0.05)'
      : 'rgba(16,185,129,0.05)',
  }),
  alertChipIcon: (variant) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    background: variant === 'danger'
      ? tokens.dangerLight
      : variant === 'warning'
      ? tokens.orangeLight
      : tokens.successLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  }),
  alertChipLabel: (variant) => ({
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 2,
    color: variant === 'danger' ? '#f87171' : variant === 'warning' ? '#fb923c' : '#34d399',
  }),
  alertChipDesc: {
    fontSize: 12,
    color: tokens.textMuted,
  },

  // ── Hero / Greeting ───────────────────────────────────────────────
  heroSection: {
    padding: '32px 16px 0',
    maxWidth: 1280,
    margin: '0 auto',
  },
  greeting: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: '-0.5px',
    marginBottom: 4,
    color: tokens.text,
  },
  greetingSub: {
    fontSize: 14,
    color: tokens.textMuted,
    marginBottom: 32,
  },

  // ── Stat cards row ────────────────────────────────────────────────
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    padding: '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    transition: 'border-color 0.15s',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: tokens.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  statValue: {
    fontSize: 30,
    fontWeight: 700,
    letterSpacing: '-1px',
    lineHeight: 1,
    color: tokens.text,
  },
  statSub: {
    fontSize: 12,
    color: tokens.textMuted,
  },
  statBar: {
    height: 3,
    borderRadius: 99,
    background: tokens.border,
    overflow: 'hidden',
    marginTop: 4,
  },
  statBarFill: {
    height: '100%',
    borderRadius: 99,
    background: `linear-gradient(90deg, ${tokens.accent}, #818cf8)`,
    transition: 'width 0.5s ease',
  },

  // ── Weekly workload card ──────────────────────────────────────────
  workloadCard: {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    padding: '22px 24px',
    marginBottom: 32,
    display: 'flex',
    alignItems: 'center',
    gap: 28,
    flexWrap: 'wrap',
  },
  workloadLeft: {
    flex: 1,
    minWidth: 200,
  },
  workloadTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: tokens.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  workloadHours: {
    fontSize: 42,
    fontWeight: 800,
    letterSpacing: '-2px',
    color: tokens.text,
    lineHeight: 1,
  },
  workloadUnit: {
    fontSize: 16,
    fontWeight: 400,
    color: tokens.textMuted,
    marginLeft: 6,
  },
  workloadDesc: {
    fontSize: 13,
    color: tokens.textMuted,
    marginTop: 6,
  },
  workloadBuckets: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  workloadBucket: {
    background: tokens.bg,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radiusSm,
    padding: '10px 16px',
    textAlign: 'center',
    minWidth: 80,
  },
  workloadBucketNum: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: '-0.5px',
  },
  workloadBucketLabel: {
    fontSize: 11,
    color: tokens.textMuted,
    marginTop: 2,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },

  // ── Main two-col layout ───────────────────────────────────────────
  mainLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 340px',
    gap: 24,
    alignItems: 'start',
    maxWidth: 1280,
    margin: '0 auto',
    padding: '0 16px',
  },

  // ── Section headers ───────────────────────────────────────────────
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '-0.2px',
    color: tokens.text,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitleIcon: {
    fontSize: 16,
  },
  sectionBadge: (color) => ({
    background: color || tokens.accentLight,
    color: color ? '#fff' : tokens.accent,
    borderRadius: 6,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
  }),

  // ── View tabs (filter chips) ──────────────────────────────────────
  tabRow: {
    display: 'flex',
    gap: 6,
    marginBottom: 16,
  },
  tab: (active) => ({
    padding: '6px 14px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    border: `1px solid ${active ? tokens.accent : tokens.border}`,
    background: active ? tokens.accentLight : 'transparent',
    color: active ? '#a5b4fc' : tokens.textDim,
    transition: 'all 0.15s',
  }),

  // ── Search bar ───────────────────────────────────────────────────
  searchBar: {
    width: '100%',
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radiusSm,
    color: tokens.text,
    fontSize: 14,
    padding: '10px 14px',
    outline: 'none',
    marginBottom: 16,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  },

  // ── Left feed panel ───────────────────────────────────────────────
  feedPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },

  // ── Collapsible section card ──────────────────────────────────────
  sectionCard: {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    overflow: 'hidden',
  },
  sectionCardHeader: {
    padding: '14px 18px',
    borderBottom: `1px solid ${tokens.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  // ── Right sidebar ─────────────────────────────────────────────────
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  sideCard: {
    background: tokens.surface,
    border: `1px solid ${tokens.border}`,
    borderRadius: tokens.radius,
    overflow: 'hidden',
  },
  sideCardHeader: {
    padding: '14px 18px',
    borderBottom: `1px solid ${tokens.border}`,
    fontSize: 13,
    fontWeight: 700,
    color: tokens.text,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sideCardBody: {
    padding: '16px 18px',
  },

  // ── Clear date btn ────────────────────────────────────────────────
  clearDateBtn: {
    background: tokens.dangerLight,
    border: `1px solid rgba(239,68,68,0.25)`,
    color: '#fca5a5',
    borderRadius: tokens.radiusSm,
    padding: '7px 14px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: 16,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  // ── Loading / skeleton ────────────────────────────────────────────
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: tokens.textMuted,
    fontSize: 14,
  },

  // ── Progress bar (streak/weekly) ──────────────────────────────────
  progressWrap: {
    padding: '16px 18px',
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 12,
    color: tokens.textMuted,
    marginBottom: 8,
  },
  progressTrack: {
    height: 6,
    borderRadius: 99,
    background: tokens.border,
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressFill: (pct, color) => ({
    height: '100%',
    width: `${Math.min(pct, 100)}%`,
    borderRadius: 99,
    background: color || `linear-gradient(90deg, ${tokens.accent}, #818cf8)`,
    transition: 'width 0.6s ease',
  }),

  // ── Streak badge ──────────────────────────────────────────────────
  streakBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(249,115,22,0.12)',
    border: '1px solid rgba(249,115,22,0.25)',
    borderRadius: 8,
    padding: '6px 12px',
    fontSize: 13,
    fontWeight: 600,
    color: '#fb923c',
  },
};

// ── Lightweight wrapper components ────────────────────────────────────────────

function StatCard({ label, value, sub, fillPct, accentColor }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: accentColor || tokens.text }}>{value}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
      {fillPct !== undefined && (
        <div style={styles.statBar}>
          <div style={{ ...styles.statBarFill, width: `${Math.min(fillPct, 100)}%`, background: accentColor ? `${accentColor}` : undefined }} />
        </div>
      )}
    </div>
  );
}

function SectionWrapper({ icon, title, count, badgeColor, children }) {
  return (
    <div style={styles.sectionCard}>
      <div style={styles.sectionCardHeader}>
        <div style={styles.sectionTitle}>
          <span style={styles.sectionTitleIcon}>{icon}</span>
          {title}
        </div>
        {count !== undefined && count > 0 && (
          <span style={styles.sectionBadge(badgeColor)}>{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [allAssignments, setAllAssignments] = useState([]);
  const [completedAssignments, setCompletedAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeklyStats, setWeeklyStats] = useState({
    completedThisWeek: 0,
    totalThisWeek: 0,
    overallCompleted: 0,
    overallTotal: 0,
  });
  const [filterView, setFilterView] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [darkMode, setDarkMode] = useState(true); // default dark for premium feel
  const [notifications, setNotifications] = useState([]);
  const [streak, setStreak] = useState(() => loadStreak().count);
  const [canvasTokenInput, setCanvasTokenInput] = useState('');
  const [savingCanvasToken, setSavingCanvasToken] = useState(false);
  const [canvasTokenError, setCanvasTokenError] = useState('');
  const [hasCanvasToken, setHasCanvasToken] = useState(null);
  const [canvasSuccess, setCanvasSuccess] = useState(false);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth < 1024;

  const toggleDarkMode = () => setDarkMode(!darkMode);
  const navigate = useNavigate();
  const handleLogout = async () => {
    try { await signOut(auth); navigate('/login'); }
    catch (err) { console.error('Logout failed:', err); }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          console.warn('No auth user yet, skipping fetchDashboardData');
          return;
        }
        const uid = user.uid;

        const coursesRes = await fetch(`${API_BASE}/api/courses`, {
          headers: { 'x-user-id': uid },
        });

        if (!coursesRes.ok) {
          setHasCanvasToken(false);
          setCanvasTokenError('');
          setLoading(false);
          return;
        }

        setHasCanvasToken(true);
        const courses = await coursesRes.json();

        const perCourse = await Promise.all(
          courses.map(async (course) => {
            const r = await fetch(`${API_BASE}/api/courses/${course.id}/submissions`, {
              headers: { 'x-user-id': uid },
            });
            if (!r.ok) throw new Error(await r.text());
            const items = await r.json();
            return items.map((s) => ({
              ...s,
              courseName: course.name,
              courseId: course.id,
              isSubmitted:
                !!s.submitted_at ||
                s.workflow_state === 'submitted' ||
                s.workflow_state === 'graded',
            }));
          })
        );

        const merged = perCourse.flat();

        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(endOfWeek.getDate() + 7);
        endOfWeek.setHours(23, 59, 59, 999);

        const thisWeek = merged.filter(
          (a) => a.due_at && new Date(a.due_at) >= startOfWeek && new Date(a.due_at) <= endOfWeek
        );
        const completedThisWeek = thisWeek.filter((a) => a.isSubmitted).length;
        const overallCompleted = merged.filter((a) => a.isSubmitted).length;
        const overallTotal = merged.length;

        setWeeklyStats({
          completedThisWeek,
          totalThisWeek: thisWeek.length,
          overallCompleted,
          overallTotal,
        });

        const completed = merged.filter((a) => a.isSubmitted);
        const incomplete = merged.filter((a) => !a.isSubmitted);
        const sortedIncomplete = incomplete.sort(
          (a, b) => new Date(a.due_at || 0) - new Date(b.due_at || 0)
        );
        const sortedCompleted = completed.sort(
          (a, b) => new Date(b.due_at || 0) - new Date(a.due_at || 0)
        );

        setAllAssignments(sortedIncomplete);
        setCompletedAssignments(sortedCompleted);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => { setStreak(loadStreak().count); }, []);

  useEffect(() => {
    const newReminders = [];
    allAssignments.forEach((a) => {
      const dueDate = new Date(a.due_at);
      const hoursUntilDue = (dueDate - new Date()) / (1000 * 60 * 60);
      if (hoursUntilDue <= 24 && hoursUntilDue > 0) {
        newReminders.push({ type: 'dueSoon', message: `⏰ "${a.name}" is due in less than 24 hours.` });
      } else if (hoursUntilDue <= 0) {
        newReminders.push({ type: 'overdue', message: `⚠️ "${a.name}" is overdue!` });
      }
    });
    setNotifications(newReminders);
  }, [allAssignments]);

  // ---------- BUCKETS ----------
  const now = new Date();
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);
  const sevenDaysFromNow = new Date(now);
  sevenDaysFromNow.setDate(now.getDate() + 7);

  const overdue = allAssignments.filter((a) => new Date(a.due_at) < now);
  const dueToday = allAssignments.filter(
    (a) => new Date(a.due_at).toDateString() === now.toDateString()
  );
  const upcoming = allAssignments.filter((a) => {
    const dueDate = new Date(a.due_at);
    return dueDate > endOfToday && dueDate <= sevenDaysFromNow;
  });

  // ---------- SEARCH / DATE ----------
  const matchesSearch = (a) =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.courseName.toLowerCase().includes(searchQuery.toLowerCase());

  const matchesDate = (a) =>
    !selectedDate || new Date(a.due_at).toDateString() === selectedDate.toDateString();

  const hoveredAssignments = hoveredDate
    ? [...allAssignments, ...completedAssignments].filter(
        (a) => new Date(a.due_at).toDateString() === hoveredDate.toDateString()
      )
    : [];

  // ---------- UI MAPPING ----------
  const nowForUI = new Date();
  const uiAssignments = [...allAssignments, ...completedAssignments].map((a) => {
    const due = new Date(a.due_at);
    const hours = (due - nowForUI) / (1000 * 60 * 60);
    let status = 'upcoming';
    if (a.isSubmitted) status = 'completed';
    else if (due < nowForUI) status = 'overdue';
    else if (hours <= 24) status = 'dueSoon';
    return {
      id: a.id,
      title: a.name,
      course: a.courseName,
      dueAt: a.due_at,
      html_url: a.html_url,
      isSubmitted: !!a.isSubmitted,
      status,
      ...a,
    };
  });

  const counts = {
    upcoming: dueToday.length + upcoming.length,
    overdue: overdue.length,
    completed: completedAssignments.length,
  };

  // ---------- WORKLOAD ESTIMATE ----------
  const incompleteThisWeek = weeklyStats.totalThisWeek - weeklyStats.completedThisWeek;
  const estimatedHours = estimateWeeklyHours(incompleteThisWeek, overdue.length);
  const weeklyCompletionPct = weeklyStats.totalThisWeek > 0
    ? Math.round((weeklyStats.completedThisWeek / weeklyStats.totalThisWeek) * 100)
    : 0;
  const overallCompletionPct = weeklyStats.overallTotal > 0
    ? Math.round((weeklyStats.overallCompleted / weeklyStats.overallTotal) * 100)
    : 0;

  function handleMarkDone(a) {
    const today = new Date();
    const s = loadStreak();
    if (isSameDay(s.lastDate, today)) {
      saveStreak({ ...s, lastDate: today });
    } else if (!s.lastDate || isYesterday(s.lastDate, today)) {
      const next = { count: (s.count || 0) + 1, lastDate: today };
      saveStreak(next);
      setStreak(next.count);
    } else {
      const reset = { count: 1, lastDate: today };
      saveStreak(reset);
      setStreak(reset.count);
    }
  }

  // ---------- GUARDS ----------
  if (hasCanvasToken === null) {
    return (
      <div style={{ ...styles.page, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center', color: tokens.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: 14 }}>Loading your workspace…</div>
        </div>
      </div>
    );
  }

  if (!hasCanvasToken) {
    return (
      <Onboarding
        token={canvasTokenInput}
        setToken={setCanvasTokenInput}
        onSave={async () => {
          const user = auth.currentUser;
          if (!user) return;
          try {
            setSavingCanvasToken(true);
            setCanvasTokenError("");
            const res = await fetch(
              "https://us-central1-myco-tech.cloudfunctions.net/saveCanvasToken",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: user.uid, canvasToken: canvasTokenInput }),
              }
            );
            if (!res.ok) {
              const data = await res.json().catch(() => ({}));
              throw new Error(data.error || "Failed to save token");
            }
            setHasCanvasToken(true);
            setCanvasSuccess(true);
            setTimeout(() => { window.location.reload(); }, 1000);
          } catch (err) {
            console.error(err);
            setCanvasTokenError("That token didn't work. Try generating a new one in Canvas.");
          } finally {
            setSavingCanvasToken(false);
          }
        }}
        saving={savingCanvasToken}
        error={canvasTokenError}
        success={canvasSuccess}
      />
    );
  }

  const userName = auth.currentUser?.displayName?.split(' ')[0] || '';
  const todayStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      {/* ── Google Font import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { overflow-x: hidden; max-width: 100vw; }

        /* ── Responsive tweaks ── */

        /* Tablet (≤1024px) */
        @media (max-width: 1024px) {
          .dq-main-layout  { grid-template-columns: 1fr !important; padding: 0 20px !important; }
          .dq-stats-row    { grid-template-columns: repeat(2, 1fr) !important; }
          .dq-hero         { padding: 28px 20px 0 !important; }
          .dq-topbar       { padding: 12px 20px !important; }
          .dq-sidebar      { margin-top: 8px; }
        }

        /* Mobile (≤768px) */
        @media (max-width: 768px) {
          /* Topbar */
          .dq-topbar       { padding: 10px 16px !important; min-height: 52px; }
          .dq-topbar-right { gap: 6px !important; flex-wrap: nowrap; }
          .dq-streak-badge { display: none !important; }
          .dq-user-email   { display: none !important; }
          .dq-logo-sub     { display: none !important; }
          .dq-logo-sep     { display: none !important; }

          /* Hero */
          .dq-hero         { padding: 20px 16px 0 !important; }
          .dq-greeting     { font-size: 22px !important; margin-bottom: 2px; }
          .dq-greeting-sub { font-size: 12px !important; margin-bottom: 20px !important; }

          /* Stats */
          .dq-stats-row    { grid-template-columns: repeat(2, 1fr) !important; gap: 10px !important; margin-bottom: 16px !important; }
          .dq-stat-value   { font-size: 26px !important; }

          /* Workload card */
          .dq-workload-card    { flex-direction: column !important; gap: 16px !important; padding: 16px !important; margin-bottom: 20px !important; }
          .dq-workload-left    { min-width: 0 !important; }
          .dq-workload-hours   { font-size: 34px !important; }
          .dq-workload-buckets { display: grid !important; grid-template-columns: repeat(4, 1fr) !important; gap: 8px !important; width: 100%; }
          .dq-workload-bucket  { min-width: 0 !important; padding: 8px 10px !important; }
          .dq-workload-bucket-num { font-size: 18px !important; }

          /* Main layout */
          .dq-main-layout  { grid-template-columns: 1fr !important; padding: 0 16px !important; gap: 16px !important; }

          /* Feed panel */
          .dq-feed-panel   { gap: 16px !important; }

          /* Sidebar */
          .dq-sidebar      { gap: 14px !important; margin-top: 0; }
        }

        /* Small mobile (≤480px) */
        @media (max-width: 480px) {
          /* Topbar — icon-only mode */
          .dq-topbar       { padding: 8px 12px !important; }
          .dq-logout-text  { display: none !important; }

          /* Hero */
          .dq-hero         { padding: 16px 12px 0 !important; }
          .dq-greeting     { font-size: 20px !important; }
          .dq-greeting-sub { font-size: 11px !important; }

          /* Stats */
          .dq-stats-row    { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
          .dq-stat-card    { padding: 12px 14px !important; gap: 6px !important; }
          .dq-stat-label   { font-size: 10px !important; }
          .dq-stat-value   { font-size: 24px !important; }
          .dq-stat-sub     { font-size: 11px !important; }

          /* Workload */
          .dq-workload-hours { font-size: 30px !important; }
          .dq-workload-title { font-size: 11px !important; }
          .dq-workload-desc  { font-size: 11px !important; }

          /* Tabs */
          .dq-tab-row      { flex-wrap: wrap !important; gap: 4px !important; }
          .dq-tab          { font-size: 12px !important; padding: 5px 10px !important; }

          /* Main layout */
          .dq-main-layout  { padding: 0 12px !important; }
        }

        /* Override react-calendar for dark theme */
        .dq-cal .react-calendar {
          background: transparent;
          border: none;
          width: 100%;
          font-family: 'DM Sans', sans-serif;
          color: #e8eaf0;
        }
        .dq-cal .react-calendar__navigation button {
          color: #e8eaf0;
          background: none;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
        }
        .dq-cal .react-calendar__navigation button:hover,
        .dq-cal .react-calendar__navigation button:focus {
          background: rgba(255,255,255,0.06);
          border-radius: 6px;
        }
        .dq-cal .react-calendar__month-view__weekdays {
          font-size: 10px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .dq-cal .react-calendar__month-view__weekdays abbr {
          text-decoration: none;
        }
        .dq-cal .react-calendar__tile {
          color: #9ca3af;
          background: none;
          font-family: 'DM Sans', sans-serif;
          font-size: 12px;
          border-radius: 6px;
          padding: 6px 4px;
          position: relative;
        }
        .dq-cal .react-calendar__tile:hover {
          background: rgba(255,255,255,0.07);
          color: #e8eaf0;
        }
        .dq-cal .react-calendar__tile--active,
        .dq-cal .react-calendar__tile--active:hover {
          background: rgba(99,102,241,0.25);
          color: #a5b4fc;
        }
        .dq-cal .react-calendar__tile--now {
          background: rgba(99,102,241,0.12);
          color: #e8eaf0;
          font-weight: 700;
        }
        .dq-cal .react-calendar__month-view__days__day--neighboringMonth {
          color: #374151;
        }
        .due-dot {
          position: absolute;
          bottom: 2px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 10px;
          color: #6366f1;
          line-height: 1;
        }

        /* Scrollbars */
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; }

        /* Input placeholder */
        input::placeholder { color: #4b5563; }

        /* Topbar search focus */
        .dq-search:focus { border-color: rgba(99,102,241,0.5) !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }

        /* Stat card hover */
        .dq-stat-card:hover { border-color: rgba(255,255,255,0.15) !important; }

        /* Tab hover */
        .dq-tab:hover { border-color: rgba(255,255,255,0.15) !important; color: #e8eaf0 !important; }

        /* Bell button hover */
        button[aria-label="Alerts"]:hover { background: rgba(255,255,255,0.08) !important; color: #e8eaf0 !important; }

        /* Logout hover */
        .dq-logout-hover:hover { background: rgba(239,68,68,0.08) !important; border-color: rgba(239,68,68,0.25) !important; color: #f87171 !important; }
      `}</style>

      {/* ── Header bar — greeting + actions ──────────────────────────── */}
      <header style={{ ...styles.topbar, padding: isMobile ? '10px 16px' : isTablet ? '12px 20px' : '16px 32px' }} className="dq-topbar">

        {/* Left: greeting */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <div style={{
            fontSize: isMobile ? 17 : isTablet ? 19 : 21,
            fontWeight: 700,
            letterSpacing: '-0.4px',
            color: tokens.text,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {getGreeting()}{userName ? `, ${userName}` : ''} 👋
          </div>
          {!isMobile && (
            <div style={{ fontSize: 12, color: tokens.textMuted, lineHeight: 1 }}>
              {todayStr}
            </div>
          )}
        </div>

        {/* Right: actions */}
        <div style={{ ...styles.topbarRight, gap: isMobile ? 4 : 8 }} className="dq-topbar-right">

          {/* Streak badge — hide on mobile */}
          {streak > 0 && (
            <div style={styles.streakBadge} className="dq-streak-badge">
              🔥 {streak}-day streak
            </div>
          )}

          {/* Alert bell with dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              style={styles.bellBtn(overdue.length > 0)}
              onClick={() => setAlertsOpen(v => !v)}
              aria-label="Alerts"
              title="View alerts"
            >
              <Bell size={15} />
              {overdue.length > 0 && <span style={styles.bellDot} />}
            </button>

            <AnimatePresence>
              {alertsOpen && (
                <motion.div
                  style={styles.alertsPanel}
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                >
                  <div style={styles.alertsPanelHeader}>
                    <span style={styles.alertsPanelTitle}>Notifications</span>
                    <button style={styles.alertsCloseBtn} onClick={() => setAlertsOpen(false)} aria-label="Close">
                      <X size={13} />
                    </button>
                  </div>

                  {/* Overdue row */}
                  <div style={styles.alertChip(overdue.length > 0 ? 'danger' : 'success')}>
                    <div style={styles.alertChipIcon(overdue.length > 0 ? 'danger' : 'success')}>
                      {overdue.length > 0
                        ? <AlertTriangle size={13} color="#f87171" />
                        : <CheckCircle  size={13} color="#34d399" />}
                    </div>
                    <div>
                      <div style={styles.alertChipLabel(overdue.length > 0 ? 'danger' : 'success')}>Overdue</div>
                      <div style={styles.alertChipDesc}>
                        {overdue.length === 0
                          ? "Nothing overdue — you're all caught up!"
                          : `${overdue.length} assignment${overdue.length !== 1 ? 's' : ''} need attention`}
                      </div>
                    </div>
                  </div>

                  {/* Due soon row */}
                  {(() => {
                    const dueSoon = notifications.filter(n => n.type === 'dueSoon');
                    const variant = dueSoon.length > 0 ? 'warning' : 'success';
                    return (
                      <div style={styles.alertChip(variant)}>
                        <div style={styles.alertChipIcon(variant)}>
                          {dueSoon.length > 0
                            ? <Clock        size={13} color="#fb923c" />
                            : <CheckCircle  size={13} color="#34d399" />}
                        </div>
                        <div>
                          <div style={styles.alertChipLabel(variant)}>Due in &lt;24h</div>
                          <div style={styles.alertChipDesc}>
                            {dueSoon.length === 0
                              ? 'No deadlines in the next 24 hours'
                              : `${dueSoon.length} assignment${dueSoon.length !== 1 ? 's' : ''} coming up soon`}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* User identity pill */}
          {auth.currentUser && (
            <div style={styles.userPill} title={auth.currentUser.email || ''}>
              <div style={styles.userAvatar}>
                {(auth.currentUser.displayName || auth.currentUser.email || 'U')[0].toUpperCase()}
              </div>
              {auth.currentUser.email && (
                <span style={styles.userEmail} className="dq-user-email">{auth.currentUser.email}</span>
              )}
            </div>
          )}

          {/* Dark mode toggle */}
          <button style={styles.darkToggleBtn} className="dq-tab" onClick={toggleDarkMode}>
            {darkMode ? '☀️' : '🌙'}
          </button>

          {/* Log out */}
          <button style={styles.logoutBtn} className="dq-logout-hover" onClick={handleLogout}>
            <LogOut size={12} /><span className="dq-logout-text" style={{ marginLeft: 4 }}>Log out</span>
          </button>
        </div>
      </header>

      {/* ── Hero Section ─────────────────────────────────────────────── */}
      <div style={{ ...styles.heroSection, padding: isMobile ? '16px 16px 0' : isTablet ? '20px 20px 0' : '24px 32px 0' }} className="dq-hero">

        {/* ── Stats Row ──────────────────────────────────────────────── */}
        <div style={{ ...styles.statsRow, gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 10 : 12, marginBottom: isMobile ? 16 : 24 }} className="dq-stats-row">
          <div className="dq-stat-card" style={{ ...styles.statCard, padding: isMobile ? '12px 14px' : '20px 22px', gap: isMobile ? 6 : 8 }}>
            <div style={{ ...styles.statLabel, fontSize: isMobile ? 10 : 12 }} className="dq-stat-label">Due This Week</div>
            <div className="dq-stat-value" style={{ ...styles.statValue, fontSize: isMobile ? 24 : 30, color: counts.upcoming > 0 ? '#a5b4fc' : tokens.success }}>
              {counts.upcoming}
            </div>
            <div style={styles.statSub} className="dq-stat-sub">
              {weeklyStats.completedThisWeek} of {weeklyStats.totalThisWeek} completed
            </div>
            <div style={styles.statBar}>
              <div style={{ ...styles.statBarFill, width: `${weeklyCompletionPct}%` }} />
            </div>
          </div>

          <div className="dq-stat-card" style={{ ...styles.statCard, padding: isMobile ? '12px 14px' : '20px 22px', gap: isMobile ? 6 : 8 }}>
            <div style={{ ...styles.statLabel, fontSize: isMobile ? 10 : 12 }} className="dq-stat-label">Overdue</div>
            <div className="dq-stat-value" style={{ ...styles.statValue, fontSize: isMobile ? 24 : 30, color: overdue.length > 0 ? '#f87171' : tokens.success }}>
              {overdue.length}
            </div>
            <div style={styles.statSub} className="dq-stat-sub">
              {overdue.length === 0 ? 'All caught up 🎉' : 'Need attention'}
            </div>
          </div>

          <div className="dq-stat-card" style={{ ...styles.statCard, padding: isMobile ? '12px 14px' : '20px 22px', gap: isMobile ? 6 : 8 }}>
            <div style={{ ...styles.statLabel, fontSize: isMobile ? 10 : 12 }} className="dq-stat-label">Overall Progress</div>
            <div className="dq-stat-value" style={{ ...styles.statValue, fontSize: isMobile ? 24 : 30, color: tokens.success }}>
              {overallCompletionPct}%
            </div>
            <div style={styles.statSub} className="dq-stat-sub">
              {weeklyStats.overallCompleted}/{weeklyStats.overallTotal} assignments done
            </div>
            <div style={styles.statBar}>
              <div style={{ ...styles.statBarFill, width: `${overallCompletionPct}%`, background: tokens.success }} />
            </div>
          </div>

          <div className="dq-stat-card" style={{ ...styles.statCard, padding: isMobile ? '12px 14px' : '20px 22px', gap: isMobile ? 6 : 8 }}>
            <div style={{ ...styles.statLabel, fontSize: isMobile ? 10 : 12 }} className="dq-stat-label">Streak</div>
            <div className="dq-stat-value" style={{ ...styles.statValue, fontSize: isMobile ? 24 : 30, color: '#fb923c' }}>
              {streak} <span style={{ fontSize: 20 }}>🔥</span>
            </div>
            <div style={styles.statSub} className="dq-stat-sub">
              {streak === 0 ? 'Submit something to start' : streak === 1 ? '1 day — keep it up!' : 'Days in a row'}
            </div>
          </div>
        </div>

        {/* ── Weekly Workload Estimate Card ─────────────────────────── */}
        <div style={{ ...styles.workloadCard, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 28, padding: isMobile ? '16px' : '22px 24px', marginBottom: isMobile ? 20 : 32 }} className="dq-workload-card">
          <div style={styles.workloadLeft} className="dq-workload-left">
            <div style={styles.workloadTitle} className="dq-workload-title">⏱ Estimated Time Remaining This Week</div>
            <div>
              <span style={{ ...styles.workloadHours, fontSize: isMobile ? 34 : 42 }} className="dq-workload-hours">{estimatedHours}</span>
              <span style={styles.workloadUnit}>hrs</span>
            </div>
            <div style={styles.workloadDesc} className="dq-workload-desc">
              Based on {incompleteThisWeek} incomplete assignment{incompleteThisWeek !== 1 ? 's' : ''} this week
              {overdue.length > 0 ? ` + ${overdue.length} overdue` : ''}. 
              Estimated ~2 hrs per task.
            </div>
          </div>

          <div style={{ ...styles.workloadBuckets, display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'repeat(4, 1fr)' : undefined, gap: isMobile ? 8 : 12, width: isMobile ? '100%' : undefined }} className="dq-workload-buckets">
            <div style={{ ...styles.workloadBucket, padding: isMobile ? '8px 6px' : '10px 16px', minWidth: isMobile ? 0 : 80 }} className="dq-workload-bucket">
              <div className="dq-workload-bucket-num" style={{ ...styles.workloadBucketNum, fontSize: isMobile ? 18 : 22, color: '#f87171' }}>{overdue.length}</div>
              <div style={styles.workloadBucketLabel}>Overdue</div>
            </div>
            <div style={{ ...styles.workloadBucket, padding: isMobile ? '8px 6px' : '10px 16px', minWidth: isMobile ? 0 : 80 }} className="dq-workload-bucket">
              <div className="dq-workload-bucket-num" style={{ ...styles.workloadBucketNum, fontSize: isMobile ? 18 : 22, color: '#fb923c' }}>{dueToday.length}</div>
              <div style={styles.workloadBucketLabel}>Today</div>
            </div>
            <div style={{ ...styles.workloadBucket, padding: isMobile ? '8px 6px' : '10px 16px', minWidth: isMobile ? 0 : 80 }} className="dq-workload-bucket">
              <div className="dq-workload-bucket-num" style={{ ...styles.workloadBucketNum, fontSize: isMobile ? 18 : 22, color: '#a5b4fc' }}>{upcoming.length}</div>
              <div style={styles.workloadBucketLabel}>This Week</div>
            </div>
            <div style={{ ...styles.workloadBucket, padding: isMobile ? '8px 6px' : '10px 16px', minWidth: isMobile ? 0 : 80 }} className="dq-workload-bucket">
              <div className="dq-workload-bucket-num" style={{ ...styles.workloadBucketNum, fontSize: isMobile ? 18 : 22, color: tokens.success }}>{weeklyStats.completedThisWeek}</div>
              <div style={styles.workloadBucketLabel}>Done</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main Content ─────────────────────────────────────────────── */}
      <div style={{ ...styles.mainLayout, gridTemplateColumns: isTablet ? '1fr' : '1fr 340px', padding: isMobile ? '0 16px' : isTablet ? '0 20px' : '0 32px', gap: isMobile ? 16 : 24, marginTop: isTablet ? 20 : 0 }} className="dq-main-layout">

        {/* ── Left: Assignment Feed ────────────────────────────────── */}
        <div style={{ ...styles.feedPanel, gap: isMobile ? 16 : 24 }} className="dq-feed-panel">

          {/* Search */}
          <input
            className="dq-search"
            style={styles.searchBar}
            placeholder="🔍  Search assignments or courses…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* View tabs */}
          <div style={styles.tabRow} className="dq-tab-row">
            {['upcoming', 'overdue', 'completed'].map((v) => (
              <button
                key={v}
                className="dq-tab"
                style={styles.tab(filterView === v)}
                onClick={() => setFilterView(v)}
              >
                {v === 'upcoming' && '📋 '}
                {v === 'overdue' && '⚠️ '}
                {v === 'completed' && '✅ '}
                {v.charAt(0).toUpperCase() + v.slice(1)}
                {counts[v] > 0 && (
                  <span style={{
                    marginLeft: 6,
                    background: v === 'overdue' ? tokens.dangerLight : tokens.accentLight,
                    color: v === 'overdue' ? '#f87171' : '#a5b4fc',
                    borderRadius: 4,
                    padding: '1px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                  }}>
                    {counts[v]}
                  </span>
                )}
              </button>
            ))}
          </div>

          {selectedDate && (
            <button style={styles.clearDateBtn} onClick={() => setSelectedDate(null)}>
              ✕ Clear date filter — {selectedDate.toLocaleDateString()}
            </button>
          )}

          {loading ? (
            <div style={styles.loadingState}>
              <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
              Loading your assignments…
            </div>
          ) : (
            <>
              {/* Priority Tasks */}
              <SectionWrapper icon="🎯" title="Priority Tasks" badgeColor={tokens.danger}>
                <div style={{ padding: '4px 0' }}>
                  <PriorityTasks assignments={uiAssignments} onMarkDone={handleMarkDone} />
                </div>
              </SectionWrapper>

              {/* Main Assignment Feed */}
              <SectionWrapper
                icon={filterView === 'completed' ? '✅' : filterView === 'overdue' ? '⚠️' : '📋'}
                title={filterView === 'completed' ? 'Completed' : filterView === 'overdue' ? 'Overdue' : 'Assignments'}
                count={filterView === 'upcoming' ? counts.upcoming : filterView === 'overdue' ? counts.overdue : counts.completed}
                badgeColor={filterView === 'overdue' ? tokens.danger : undefined}
              >
                <div style={{ padding: '4px 0' }}>
                  <AssignmentFeed
                    dueToday={dueToday.filter(matchesSearch).filter(matchesDate)}
                    overdue={filterView === 'upcoming' ? [] : overdue.filter(matchesSearch).filter(matchesDate)}
                    upcoming={upcoming.filter(matchesSearch).filter(matchesDate)}
                    completed={completedAssignments.filter(matchesSearch).filter(matchesDate)}
                    activeView={filterView}
                    dateFilter={selectedDate}
                  />
                </div>
              </SectionWrapper>
            </>
          )}
        </div>

        {/* ── Right Sidebar ────────────────────────────────────────── */}
        <div style={{ ...styles.sidebar, gap: isMobile ? 12 : 20 }} className="dq-sidebar">

          {/* AI Helper */}
          <div style={styles.sideCard}>
            <div style={styles.sideCardHeader}>
              <span>✨</span> AI Study Assistant
            </div>
            <div style={styles.sideCardBody}>
              <AIHelper assignments={uiAssignments} />
            </div>
          </div>

          {/* Weekly progress */}
          <div style={styles.sideCard}>
            <div style={styles.sideCardHeader}>
              <span>📊</span> Weekly Progress
            </div>
            <div style={styles.progressWrap}>
              <div style={styles.progressLabel}>
                <span>This week</span>
                <span style={{ color: tokens.text, fontWeight: 600 }}>
                  {weeklyStats.completedThisWeek} / {weeklyStats.totalThisWeek}
                </span>
              </div>
              <div style={styles.progressTrack}>
                <div style={styles.progressFill(weeklyCompletionPct)} />
              </div>
              <div style={styles.progressLabel}>
                <span>All time</span>
                <span style={{ color: tokens.text, fontWeight: 600 }}>
                  {weeklyStats.overallCompleted} / {weeklyStats.overallTotal}
                </span>
              </div>
              <div style={styles.progressTrack}>
                <div style={styles.progressFill(overallCompletionPct, tokens.success)} />
              </div>
              <div style={{ ...styles.progressWrap, padding: '12px 0 0' }}>
                <ProgressRow
                  weekly={{ done: weeklyStats.completedThisWeek, total: weeklyStats.totalThisWeek }}
                  streakDays={streak}
                />
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div style={styles.sideCard}>
            <div style={styles.sideCardHeader}>
              <span>📅</span> Calendar
            </div>
            <div style={{ padding: '14px 16px' }} className="dq-cal">
              <Calendar
                onClickDay={(value) => {
                  setSelectedDate(value);
                  setFilterView('upcoming');
                }}
                onMouseOver={(e) => {
                  const label = e.target.getAttribute('aria-label');
                  if (label) setHoveredDate(new Date(label));
                }}
                onMouseLeave={() => setHoveredDate(null)}
                tileClassName={({ date }) => {
                  const today = new Date();
                  if (date.toDateString() === today.toDateString()) return 'highlight-today';
                  return '';
                }}
                tileContent={({ date }) => {
                  const hasDue = [...allAssignments, ...completedAssignments].some(
                    (a) => new Date(a.due_at).toDateString() === date.toDateString()
                  );
                  return hasDue ? <span className="due-dot">•</span> : null;
                }}
              />

              {hoveredAssignments.length > 0 && (
                <div style={{
                  marginTop: 12,
                  background: tokens.bg,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: tokens.radiusSm,
                  padding: '10px 12px',
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                    📌 {hoveredDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  {hoveredAssignments.map((a) => (
                    <div key={a.id} style={{
                      fontSize: 12,
                      color: tokens.textDim,
                      marginBottom: 6,
                      paddingBottom: 6,
                      borderBottom: `1px solid ${tokens.border}`,
                    }}>
                      <div style={{ fontWeight: 600, color: tokens.text, marginBottom: 2 }}>{a.name}</div>
                      <div style={{ color: tokens.textMuted }}>
                        {a.courseName} · {new Date(a.due_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Canvas connection */}
          <div style={styles.sideCard}>
            <div style={styles.sideCardHeader}>
              <span>🔗</span> Canvas Connection
            </div>
            <div style={styles.sideCardBody}>
              <ConnectCanvas />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
// [END OF FILE]
